import { Component, forwardRef, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatAutocompleteModule } from '@angular/material/autocomplete';

export interface CompanyOption {
  companyId: string;
  companyName: string;
  countryCode: string;
}

@Component({
  selector: 'app-company-select',
  imports: [CommonModule, FormsModule, MatFormFieldModule, MatInputModule, MatIconModule, MatAutocompleteModule],
  templateUrl: './company-select.html',
  styleUrl: './company-select.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CompanySelect),
      multi: true,
    },
  ],
})
export class CompanySelect implements ControlValueAccessor {
  @Input() label = 'Company';
  @Input() placeholder = 'Search by name or country...';
  @Input() options: CompanyOption[] = [];

  selection: CompanyOption | string = '';
  disabled = false;

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  get filtered(): CompanyOption[] {
    const term = typeof this.selection === 'string' ? this.selection.trim().toLowerCase() : '';
    if (!term) {
      return this.options;
    }
    return this.options.filter(
      (c) => c.companyName.toLowerCase().includes(term) || c.countryCode.toLowerCase().includes(term)
    );
  }

  displayCompany = (value: CompanyOption | string): string => {
    if (!value) {
      return '';
    }
    return typeof value === 'string' ? value : `${value.companyName} (${value.countryCode})`;
  };

  onOptionSelected(company: CompanyOption): void {
    this.selection = company;
    this.onChange(company.companyId);
    this.onTouched();
  }

  writeValue(value: string): void {
    this.selection = this.options.find((c) => c.companyId === value) ?? '';
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onBlur(): void {
    this.onTouched();
  }
}
